import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Save, Upload, User, MessageCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import api from '../../api/client';

/**
 * Admin → Configurazione WhatsApp (Phase 70).
 * Numero (formato internazionale SENZA '+'), messaggio di benvenuto del box,
 * testo precompilato wa.me, avatar del box e toggle ON/OFF del widget.
 */
export const AdminWhatsAppPanel = () => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get('/admin/whatsapp-widget/config').then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  if (!cfg) {
    return (
      <div className="flex justify-center rounded-xl border border-border bg-card p-8">
        <Loader2 className="h-5 w-5 animate-spin gold-text" />
      </div>
    );
  }

  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/whatsapp-widget/config', {
        enabled: cfg.enabled,
        phone: cfg.phone,
        welcome_message: cfg.welcome_message,
        prefill_message: cfg.prefill_message,
        hours_enabled: cfg.hours_enabled,
        hours_days: cfg.hours_days,
        hours_start: cfg.hours_start,
        hours_end: cfg.hours_end,
        hours_label: cfg.hours_label,
        offline_message: cfg.offline_message,
      });
      setCfg(data);
      toast({ title: 'Configurazione WhatsApp salvata' });
    } catch (e) {
      toast({
        title: 'Salvataggio non riuscito',
        description: String(e?.response?.data?.detail || '').split('|').pop(),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/admin/whatsapp-widget/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCfg((c) => ({ ...c, avatar_url: data.avatar_url }));
      toast({ title: 'Avatar caricato' });
    } catch (e) {
      toast({
        title: 'Upload non riuscito',
        description: String(e?.response?.data?.detail || '').split('|').pop(),
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="admin-whatsapp-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <MessageCircle className="h-4 w-4 text-[#25D366]" /> Configurazione WhatsApp
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Widget di assistenza in basso a destra (sostituisce la vecchia chat interna).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold">{cfg.enabled ? 'Attivo' : 'Disattivato'}</span>
          <Switch checked={cfg.enabled} onCheckedChange={set('enabled')} data-testid="whatsapp-enabled-toggle" />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[auto,1fr]">
        {/* Avatar */}
        <div className="flex flex-col items-center gap-2">
          {cfg.avatar_url ? (
            <img
              src={cfg.avatar_url}
              alt="Avatar WhatsApp"
              className="h-20 w-20 rounded-full border-2 border-amber-500/40 object-cover"
              data-testid="whatsapp-avatar-preview"
            />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-dashed border-amber-500/40 bg-amber-500/5" data-testid="whatsapp-avatar-placeholder">
              <User className="h-8 w-8 gold-text" strokeWidth={1.5} />
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleAvatarUpload(e.target.files?.[0])}
            data-testid="whatsapp-avatar-input"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="text-xs"
            data-testid="whatsapp-avatar-upload-btn"
          >
            {uploading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1 h-3.5 w-3.5" />}
            Carica foto
          </Button>
        </div>

        {/* Fields */}
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-xs font-semibold">Numero WhatsApp</p>
            <Input
              value={cfg.phone}
              onChange={(e) => set('phone')(e.target.value.replace(/[^\d]/g, ''))}
              placeholder="393356011946"
              className="h-9 max-w-xs font-mono text-sm"
              data-testid="whatsapp-phone-input"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Formato internazionale <strong>senza il segno +</strong> (es. 393356011946).
            </p>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold">Messaggio di benvenuto (mostrato nel box)</p>
            <Textarea
              value={cfg.welcome_message}
              onChange={(e) => set('welcome_message')(e.target.value)}
              rows={3}
              className="text-sm"
              data-testid="whatsapp-welcome-input"
            />
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold">Testo precompilato su WhatsApp (opzionale)</p>
            <Input
              value={cfg.prefill_message}
              onChange={(e) => set('prefill_message')(e.target.value)}
              placeholder="Ciao Nymia, ho bisogno di assistenza."
              className="h-9 text-sm"
              data-testid="whatsapp-prefill-input"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              È il messaggio già scritto quando l&apos;utente apre la chat WhatsApp.
            </p>
          </div>
          <div className="rounded-lg border border-border bg-background/40 p-3" data-testid="whatsapp-hours-section">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold">Orari di disponibilità</p>
                <p className="text-[11px] text-muted-foreground">
                  Fuori orario il widget mostra lo stato &quot;Offline&quot; (fuso: Europe/Rome).
                </p>
              </div>
              <Switch checked={!!cfg.hours_enabled} onCheckedChange={set('hours_enabled')} data-testid="whatsapp-hours-toggle" />
            </div>
            {cfg.hours_enabled && (
              <div className="mt-3 space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((label, idx) => {
                    const active = (cfg.hours_days || []).includes(idx);
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => set('hours_days')(
                          active
                            ? (cfg.hours_days || []).filter((d) => d !== idx)
                            : [...(cfg.hours_days || []), idx].sort(),
                        )}
                        className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${
                          active ? 'gold-gradient text-black' : 'bg-secondary text-muted-foreground'
                        }`}
                        data-testid={`whatsapp-day-${idx}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-xs">
                    Dalle
                    <Input
                      type="time"
                      value={cfg.hours_start}
                      onChange={(e) => set('hours_start')(e.target.value)}
                      className="h-8 w-28 text-sm"
                      data-testid="whatsapp-hours-start"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    alle
                    <Input
                      type="time"
                      value={cfg.hours_end}
                      onChange={(e) => set('hours_end')(e.target.value)}
                      className="h-8 w-28 text-sm"
                      data-testid="whatsapp-hours-end"
                    />
                  </label>
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold">Etichetta orari (mostrata nel box)</p>
                  <Input
                    value={cfg.hours_label}
                    onChange={(e) => set('hours_label')(e.target.value)}
                    placeholder="Rispondiamo Lun–Ven, 9:00–18:00"
                    className="h-8 text-sm"
                    data-testid="whatsapp-hours-label-input"
                  />
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold">Messaggio fuori orario</p>
                  <Input
                    value={cfg.offline_message}
                    onChange={(e) => set('offline_message')(e.target.value)}
                    placeholder="In questo momento siamo offline: scrivici comunque…"
                    className="h-8 text-sm"
                    data-testid="whatsapp-offline-message-input"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-1">
            <Button
              onClick={handleSave}
              disabled={saving}
              size="sm"
              className="gold-gradient text-black"
              data-testid="whatsapp-save-btn"
            >
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Salva configurazione
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminWhatsAppPanel;
